
import React, { useState, useMemo } from 'react';
import { FaEdit, FaTrashAlt, FaUser, FaCalendarAlt, FaCheckCircle, FaTimesCircle, FaExclamationTriangle, FaSearch, FaFilter } from 'react-icons/fa';
import type { Student } from '../../../types/student';
import { studentService } from '../../../services/student.service';
import { Timestamp } from 'firebase/firestore';

interface StudentListProps {
  students: Student[];
  classId: string;
  durationInWeeks: number;
  onRemoveStudent: (studentId: string) => void;
  onStudentDataChange: () => void;
  isLoading?: boolean;
  error?: string | null;
}

const attendanceOptions: Array<'선택' | '출석' | '결석' | '지각'> = ['선택', '출석', '결석', '지각'];
const completionOptions: Array<'선택' | '수료' | '보류'> = ['선택', '수료', '보류'];

// Helper function to convert Firestore Timestamp-like object to YYYY-MM-DD string
const formatDateForInput = (timestamp: { seconds: number; nanoseconds: number } | undefined): string => {
  if (!timestamp) return '';
  // Firestore Timestamps are often passed around as objects, even if not actual Timestamp instances client-side
  const date = new Timestamp(timestamp.seconds, timestamp.nanoseconds).toDate();
  return date.toISOString().split('T')[0];
};

// 출석 상태별 스타일
const getAttendanceStyle = (status: string) => {
  switch (status) {
    case '출석':
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
    case '결석':
      return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
    case '지각':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
    default:
      return 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400';
  }
};

// 출석 상태별 아이콘
const getAttendanceIcon = (status: string) => {
  switch (status) {
    case '출석':
      return <FaCheckCircle className="h-3 w-3" />;
    case '결석':
      return <FaTimesCircle className="h-3 w-3" />;
    case '지각':
      return <FaExclamationTriangle className="h-3 w-3" />;
    default:
      return null;
  }
};

// 수료 상태별 스타일
const getCompletionStyle = (status: string) => {
  switch (status) {
    case '수료':
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
    case '보류':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
    default:
      return 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400';
  }
};

export const StudentList: React.FC<StudentListProps> = ({
  students,
  classId,
  durationInWeeks,
  onRemoveStudent,
  onStudentDataChange,
  isLoading = false,
  error = null,
}) => {
  const [editingStates, setEditingStates] = useState<Record<string, { loading?: boolean; error?: string }>>({});
  const [searchTerm, setSearchTerm] = useState('');

  // 필터링된 학생
  const filteredStudents = useMemo(() => {
    return students.filter(student =>
      student.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [students, searchTerm]);

  // 통계 계산
  const statistics = useMemo(() => {
    const totalStudents = students.length;
    const completedStudents = students.filter(s => s.completionStatus === '수료').length;
    const attendanceRates = students.map(student => {
      const totalWeeks = durationInWeeks;
      const attendedWeeks = Object.values(student.attendance || {}).filter(status => status === '출석').length;
      return totalWeeks > 0 ? (attendedWeeks / totalWeeks) * 100 : 0;
    });
    const averageAttendanceRate = attendanceRates.length > 0
      ? attendanceRates.reduce((sum, rate) => sum + rate, 0) / attendanceRates.length
      : 0;

    return { totalStudents, completedStudents, averageAttendanceRate };
  }, [students, durationInWeeks]);

  const handleUpdate = async (studentId: string, action: () => Promise<void>) => {
    setEditingStates(prev => ({ ...prev, [studentId]: { loading: true } }));
    try {
      await action();
      onStudentDataChange(); // Refresh data in parent component
      setEditingStates(prev => ({ ...prev, [studentId]: { loading: false } }));
    } catch (error) {
      console.error("Update failed:", error);
      setEditingStates(prev => ({ ...prev, [studentId]: { loading: false, error: '업데이트 실패' } }));
    }
  };

  const handleRegistrationDateChange = (studentId: string, newDate: string) => {
    if (!newDate) return; // Prevent update if date is cleared (or handle as delete if needed)
    handleUpdate(studentId, () => studentService.updateStudentRegistrationDate(classId, studentId, newDate));
  };

  const handleAttendanceChange = (studentId: string, weekNumber: number, status: typeof attendanceOptions[number]) => {
    if (status === '선택') return; // Do not update if '선택' is chosen
    handleUpdate(studentId, () => studentService.updateStudentAttendance(classId, studentId, weekNumber, status as '출석' | '결석' | '지각'));
  };

  const handleCompletionStatusChange = (studentId: string, status: typeof completionOptions[number]) => {
     if (status === '선택') return; // Do not update if '선택' is chosen
    handleUpdate(studentId, () => studentService.updateStudentCompletionStatus(classId, studentId, status as '수료' | '보류'));
  };

  // 로딩 상태 처리
  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-8">
        <div className="space-y-4">
          {[...Array(5)].map((_, index) => (
            <div key={index} className="animate-pulse">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-1/2"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // 오류 상태 처리
  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/20">
            <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">데이터 로딩 오류</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            다시 시도
          </button>
        </div>
      </div>
    );
  }

  if (students.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-8 text-center">
        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-gray-100 dark:bg-gray-700">
          <FaUser className="h-8 w-8 text-gray-400 dark:text-gray-500" />
        </div>
        <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">등록된 수강생이 없습니다</h3>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          양육 과정에 수강생을 추가해주세요.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
      {/* 헤더 및 통계 */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">수강생 관리</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              총 {statistics.totalStudents}명의 수강생이 등록되어 있습니다.
            </p>
          </div>
          
          {/* 검색창 */}
          <div className="relative max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaSearch className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="수강생 이름으로 검색..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-150"
            />
          </div>
        </div>
        
        {/* 통계 정보 */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
            <div className="flex items-center">
              <FaUser className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-2" />
              <div>
                <p className="text-sm font-medium text-blue-800 dark:text-blue-300">총 학생 수</p>
                <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">{statistics.totalStudents}명</p>
              </div>
            </div>
          </div>
          
          <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
            <div className="flex items-center">
              <FaCheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mr-2" />
              <div>
                <p className="text-sm font-medium text-green-800 dark:text-green-300">수료 학생 수</p>
                <p className="text-2xl font-bold text-green-900 dark:text-green-100">{statistics.completedStudents}명</p>
              </div>
            </div>
          </div>
          
          <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded-lg">
            <div className="flex items-center">
              <FaCalendarAlt className="h-5 w-5 text-purple-600 dark:text-purple-400 mr-2" />
              <div>
                <p className="text-sm font-medium text-purple-800 dark:text-purple-300">평균 출석률</p>
                <p className="text-2xl font-bold text-purple-900 dark:text-purple-100">{statistics.averageAttendanceRate.toFixed(1)}%</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Table */}
      <div className="overflow-x-auto hidden md:block">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th scope="col" className="px-3 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">No.</th>
              <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">이름</th>
              <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">등록일</th>
              {[...Array(durationInWeeks)].map((_, i) => (
                <th key={`week-header-${i + 1}`} scope="col" className="px-2 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider w-20">
                  {i + 1}주차
                </th>
              ))}
              <th scope="col" className="px-3 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider w-24">수료여부</th>
              <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">관리</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {filteredStudents.map((student, index) => (
              <tr key={student.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150" role="row">
                <td className="px-3 py-3 whitespace-nowrap text-sm text-center text-gray-500 dark:text-gray-400" role="cell">{index + 1}</td>
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-center text-gray-900 dark:text-gray-200" role="cell">
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                      {student.name.charAt(0)}
                    </div>
                    <span>{student.name}</span>
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-center" role="cell">
                  <div className="relative">
                    <input
                      type="date"
                      defaultValue={formatDateForInput(student.createdAt)}
                      onBlur={(e) => handleRegistrationDateChange(student.id, e.target.value)}
                      disabled={editingStates[student.id]?.loading}
                      className="p-1.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 rounded-md shadow-sm text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full max-w-[120px]"
                      aria-label={`${student.name} 등록일`}
                    />
                  </div>
                </td>
                {[...Array(durationInWeeks)].map((_, i) => {
                  const weekKey = `week${i + 1}`;
                  const currentAttendance = student.attendance?.[weekKey] || '선택';
                  return (
                    <td key={`week-cell-${student.id}-${i + 1}`} className="px-2 py-3 whitespace-nowrap text-center" role="cell">
                      <select
                        value={currentAttendance}
                        onChange={(e) => handleAttendanceChange(student.id, i + 1, e.target.value as typeof attendanceOptions[number])}
                        disabled={editingStates[student.id]?.loading}
                        className={`p-1.5 border rounded-md shadow-sm text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full ${getAttendanceStyle(currentAttendance)}`}
                        aria-label={`${student.name} ${i + 1}주차 출석`}
                      >
                        {attendanceOptions.map(opt => (
                          <option key={opt} value={opt} disabled={opt === '선택' && currentAttendance !== '선택'}>
                            {opt === '선택' && currentAttendance !== '선택' ? '-' : opt}
                          </option>
                        ))}
                      </select>
                    </td>
                  );
                })}
                <td className="px-3 py-3 whitespace-nowrap text-center" role="cell">
                  <select
                    value={student.completionStatus || '선택'}
                    onChange={(e) => handleCompletionStatusChange(student.id, e.target.value as typeof completionOptions[number])}
                    disabled={editingStates[student.id]?.loading}
                    className={`p-1.5 border rounded-md shadow-sm text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full ${getCompletionStyle(student.completionStatus || '선택')}`}
                    aria-label={`${student.name} 수료 여부`}
                  >
                    {completionOptions.map(opt => (
                      <option key={opt} value={opt} disabled={opt === '선택' && !!student.completionStatus}>
                        {opt === '선택' && !!student.completionStatus ? '-' : opt}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-center text-sm" role="cell">
                  <div className="flex items-center justify-center space-x-2">
                    <button
                      className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded"
                      aria-label={`Edit ${student.name}`}
                      // onClick={() => onEdit(student)} // onEdit is not a prop
                    >
                      <FaEdit size={14} />
                    </button>
                    <button
                      onClick={() => onRemoveStudent(student.id)}
                      disabled={editingStates[student.id]?.loading}
                      className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 transition-colors duration-150 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-red-500 rounded"
                      aria-label={`Remove ${student.name}`}
                    >
                      <FaTrashAlt size={14} />
                    </button>
                    {editingStates[student.id]?.loading && (
                      <svg className="animate-spin h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    )}
                  </div>
                  {editingStates[student.id]?.error && (
                    <p className="text-xs text-red-500 mt-1">{editingStates[student.id]?.error}</p>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden divide-y divide-gray-200 dark:divide-gray-700">
        {filteredStudents.map((student, index) => (
          <div key={student.id} className="p-4 bg-white dark:bg-gray-800 transition-colors duration-150">
            {/* 헤더 영역 */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                  {student.name.charAt(0)}
                </div>
                <div>
                  <h3 className="text-sm font-bold text-gray-900 dark:text-white">{student.name}</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">#{index + 1}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getCompletionStyle(student.completionStatus || '선택')}`}>
                  {student.completionStatus === '수료' ? <FaCheckCircle className="h-3 w-3 mr-1" /> : null}
                  {student.completionStatus === '보류' ? <FaExclamationTriangle className="h-3 w-3 mr-1" /> : null}
                  {student.completionStatus || '미정'}
                </span>
              </div>
            </div>
            
            {/* 기본 정보 */}
            <div className="grid grid-cols-2 gap-3 text-sm mb-3">
              <div>
                <label className="text-xs text-gray-500 dark:text-gray-400 font-medium">등록일</label>
                <input
                  type="date"
                  defaultValue={formatDateForInput(student.createdAt)}
                  onBlur={(e) => handleRegistrationDateChange(student.id, e.target.value)}
                  disabled={editingStates[student.id]?.loading}
                  className="w-full p-1.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  aria-label={`${student.name} 등록일`}
                />
              </div>
            </div>
            
            {/* 주차별 출석 현황 */}
            <div className="mb-3">
              <label className="text-xs text-gray-500 dark:text-gray-400 font-medium mb-2 block">출석 현황</label>
              <div className="grid grid-cols-4 gap-1">
                {Array.from({ length: Math.min(durationInWeeks, 8) }).map((_, i) => {
                  const weekKey = `week${i + 1}`;
                  const currentAttendance = student.attendance?.[weekKey] || '선택';
                  return (
                    <div key={`week-cell-${student.id}-${i + 1}`} className="text-center">
                      <select
                        value={currentAttendance}
                        onChange={(e) => handleAttendanceChange(student.id, i + 1, e.target.value as typeof attendanceOptions[number])}
                        disabled={editingStates[student.id]?.loading}
                        className={`p-1 border rounded text-xs ${getAttendanceStyle(currentAttendance)} focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                        aria-label={`${student.name} ${i + 1}주차 출석`}
                      >
                        {attendanceOptions.map(opt => (
                          <option key={opt} value={opt} disabled={opt === '선택' && currentAttendance !== '선택'}>
                            {opt === '선택' && currentAttendance !== '선택' ? '-' : opt}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{i + 1}주차</p>
                    </div>
                  );
                })}
              </div>
              {durationInWeeks > 8 && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-center">
                  + {durationInWeeks - 8}주차 더보기
                </p>
              )}
            </div>
            
            {/* 관리 버튼 */}
            <div className="flex justify-between items-center pt-2 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-2">
                {editingStates[student.id]?.loading && (
                  <svg className="animate-spin h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {editingStates[student.id]?.error && (
                  <p className="text-xs text-red-500">{editingStates[student.id]?.error}</p>
                )}
              </div>
              <div className="flex space-x-2">
                <button
                  className="px-3 py-1 text-xs text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded transition-colors duration-150"
                  aria-label={`Edit ${student.name}`}
                >
                  수정
                </button>
                <button
                  onClick={() => onRemoveStudent(student.id)}
                  disabled={editingStates[student.id]?.loading}
                  className="px-3 py-1 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors duration-150 disabled:opacity-50"
                  aria-label={`Remove ${student.name}`}
                >
                  삭제
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
